import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { resolverHome } from '@/utils/homeRoute';

/**
 * Guard de rota: bloqueia o acesso ao /dashboard (e qualquer subrota
 * /dashboard/*) para usuários cuja única role institucional é "professor" ou
 * "aluno". Eles são redirecionados para sua home (Portal do Professor/Aluno).
 *
 * Admin e demais papéis (TI, coordenação, secretaria, etc.) continuam tendo
 * acesso normal ao Dashboard e suas subpáginas.
 *
 * Uso 1 — wrapper de elemento:
 *   <Route path="/dashboard" element={<DashboardGuard><Dashboard /></DashboardGuard>} />
 *
 * Uso 2 — wrapper de grupo de rotas (subpáginas):
 *   <Route element={<DashboardGuard />}> ... </Route>
 */
interface DashboardGuardProps {
  children?: React.ReactNode;
}

export const DashboardGuard: React.FC<DashboardGuardProps> = ({ children }) => {
  const { roles, rolesLoading } = useAuth();
  const { pathname } = useLocation();

  if (rolesLoading) return null;

  const isAdmin = roles.includes('admin');
  const isProfessor = roles.includes('professor');
  const isAluno = roles.includes('aluno');

  // Guard ativo apenas em /dashboard e suas subrotas. Outras rotas passam direto
  // — assim o componente pode ser usado como wrapper de grupos sem efeitos
  // colaterais em rotas-irmãs que eventualmente compartilhem o layout.
  const ehRotaDashboard = pathname === '/dashboard' || pathname.startsWith('/dashboard/');

  if (ehRotaDashboard && !isAdmin && (isProfessor || isAluno)) {
    return <Navigate to={resolverHome(roles)} replace />;
  }

  return <>{children ?? <Outlet />}</>;
};
